import { LoadingButton } from '@mui/lab'
import { Stack, Tab, Tabs, Typography } from '@mui/material'
import { getAccessCodesWithManagers, updateAccessCodesWithManagers } from 'api/accessCode'
import LoadingPlaceholder from 'components/placeholders/LoadingPlaceholder'
import { SyntheticEvent, useCallback, useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import {
  AccessCodeInOrganization,
  AccessCodeWithManagers,
  Nullish,
  UserInfoInOrganization,
  UserType,
  UserTypeLabels,
} from 'types'
import { UserOrgRolesManagerSelect } from '../UserOrgRolesManagerSelect/UserOrgRolesManagerSelect'
import { ManagerRole, useOrgAccessCodes } from './useOrgAccessCodes'
import * as Styled from './UserOrgRolesAccessCodes.styled'

enum OrgTableTab {
  Table = 'table',
  Structure = 'structure',
}

export function UserOrgRolesAccessCodes({
  organizationId,
  unassignedUsers,
  onUnassignedUsersRefresh,
}: {
  organizationId: number
  unassignedUsers: UserInfoInOrganization[]
  onUnassignedUsersRefresh: () => Promise<void>
}) {
  const [tableView, setTableView] = useState(OrgTableTab.Table)

  const handleChangeTableView = useCallback((_: SyntheticEvent, newValue: OrgTableTab) => {
    setTableView(newValue)
  }, [])

  const accessCodesQuery = useQuery(['accessCodesWithManagers', organizationId], () => {
    return getAccessCodesWithManagers({ organizationId })
  })

  const {
    orgStructure,
    structureChanges,
    onChangeStructure,
    onChangeCode,
    getUsersForAssign,
    checkIsStructureValid,
    onResetStructure,
    getCodesToChange,
  } = useOrgAccessCodes(accessCodesQuery.data?.data || [], unassignedUsers)

  const [highlightedCodeIds, setHighlightedCodeIds] = useState<Set<number>>(new Set())

  const handleUserChange = useCallback(
    (
      code: AccessCodeInOrganization,
      role: ManagerRole,
      selectedUser: Nullish<UserInfoInOrganization>,
    ) => {
      if (tableView === OrgTableTab.Table && role !== 'owner') {
        setHighlightedCodeIds(new Set([code.id]))
        onChangeCode(code, role, selectedUser)
        return
      }

      const affectedCodes = getCodesToChange(code, role)
      setHighlightedCodeIds(new Set(affectedCodes.map((codeItem) => codeItem.id)))
      onChangeStructure(code, role, selectedUser)
    },
    [getCodesToChange, onChangeCode, onChangeStructure, tableView],
  )

  const renderUser = useCallback(
    (user: Nullish<UserInfoInOrganization>, role: ManagerRole, code: AccessCodeInOrganization) => {
      const usersForReplace = getUsersForAssign(code, role)

      return (
        <UserOrgRolesManagerSelect
          options={usersForReplace}
          value={user}
          role={role}
          onChange={(selectedUser) => handleUserChange(code, role, selectedUser)}
        />
      )
    },
    [getUsersForAssign, handleUserChange],
  )

  const [isSaving, setIsSaving] = useState(false)

  const handleSave = useCallback(async () => {
    const isValid = checkIsStructureValid()
    if (!isValid) {
      return
    }

    setIsSaving(true)

    try {
      const codesToSave: AccessCodeWithManagers[] = []
      Object.keys(structureChanges).forEach((codeId) => {
        const changedCode: AccessCodeWithManagers = { id: Number(codeId) }
        const { owner, regionalManager, facilityManager, supervisor } = structureChanges[codeId]
        if (owner !== undefined) {
          changedCode.ownerId = owner ? owner.id : null
        }
        if (regionalManager !== undefined) {
          changedCode.regionalManagerId = regionalManager ? regionalManager.id : null
        }
        if (facilityManager !== undefined) {
          changedCode.facilityManagerId = facilityManager ? facilityManager.id : null
        }
        if (supervisor !== undefined) {
          changedCode.supervisorId = supervisor ? supervisor.id : null
        }
        codesToSave.push(changedCode)
      })

      if (codesToSave.length === 0) {
        return
      }

      await updateAccessCodesWithManagers({ accessCodes: codesToSave })
      onResetStructure()
      await onUnassignedUsersRefresh()
      await accessCodesQuery.refetch()
    } finally {
      setIsSaving(false)
    }
  }, [
    accessCodesQuery,
    checkIsStructureValid,
    onResetStructure,
    onUnassignedUsersRefresh,
    structureChanges,
  ])

  useEffect(() => {
    if (highlightedCodeIds !== null) {
      const timeout = setTimeout(() => {
        setHighlightedCodeIds(new Set())
      }, 1500)

      return () => clearTimeout(timeout)
    }
  }, [highlightedCodeIds])

  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography fontWeight={600}>Assigned Users</Typography>
        <LoadingButton
          variant="contained"
          type="button"
          loading={isSaving}
          disabled={isSaving}
          onClick={handleSave}
        >
          Save
        </LoadingButton>
      </Stack>

      <Tabs value={tableView} onChange={handleChangeTableView} indicatorColor="primary">
        <Tab value={OrgTableTab.Table} label="Table View" />
        <Tab value={OrgTableTab.Structure} label="Structure View" />
      </Tabs>

      <Styled.Table>
        {isSaving && (
          <Styled.TableLoadingOverlay>
            <LoadingPlaceholder />
          </Styled.TableLoadingOverlay>
        )}

        <Styled.TableHeader>
          <Styled.TableCell>{UserTypeLabels[UserType.ORG_OWNER]}</Styled.TableCell>
          <Styled.TableCell>{UserTypeLabels[UserType.ORG_REGIONAL_MANAGER]}</Styled.TableCell>
          <Styled.TableCell>{UserTypeLabels[UserType.ORG_FACILITY_MANAGER]}</Styled.TableCell>
          <Styled.TableCell>{UserTypeLabels[UserType.ORG_SUPERVISOR]}</Styled.TableCell>
          <Styled.TableCell>State Name / Unit Name / Access Code</Styled.TableCell>
        </Styled.TableHeader>

        {orgStructure.map((ownerGroup) => {
          return ownerGroup.regionalManagers.map((regionalGroup, regIdx) => {
            return regionalGroup.facilityManagers.map((facilityGroup, facIdx) => {
              return facilityGroup.supervisors.map((supervisorGroup, supIdx) => {
                return supervisorGroup.codes.map((code, codeIdx) => {
                  const isFirstOwnerLabel =
                    regIdx === 0 && facIdx === 0 && supIdx === 0 && codeIdx === 0
                  const isFirstRegLabel = facIdx === 0 && supIdx === 0 && codeIdx === 0
                  const isFirstFacLabel = supIdx === 0 && codeIdx === 0
                  const isFirstSupLabel = codeIdx === 0

                  const isLastReg = regIdx === ownerGroup.regionalManagers.length - 1
                  const isLastFac = facIdx === regionalGroup.facilityManagers.length - 1
                  const isLastSup = supIdx === facilityGroup.supervisors.length - 1
                  const isLastCode = codeIdx === supervisorGroup.codes.length - 1

                  const isLastOwnerCell = isLastReg && isLastFac && isLastSup && isLastCode
                  const isLastRegCell = isLastFac && isLastSup && isLastCode
                  const isLastFacCell = isLastSup && isLastCode

                  return (
                    <Styled.TableRow key={code.id} isHighlighted={highlightedCodeIds.has(code.id)}>
                      <Styled.TableCell hasBottomBorder={isLastOwnerCell}>
                        {isFirstOwnerLabel && renderUser(ownerGroup.user, 'owner', code)}
                      </Styled.TableCell>

                      {tableView === OrgTableTab.Structure ? (
                        <Styled.TableCell hasBottomBorder={isLastRegCell}>
                          {isFirstRegLabel &&
                            renderUser(regionalGroup.user, 'regionalManager', code)}
                        </Styled.TableCell>
                      ) : (
                        <Styled.TableCell hasBottomBorder>
                          {renderUser(regionalGroup.user, 'regionalManager', code)}
                        </Styled.TableCell>
                      )}

                      {tableView === OrgTableTab.Structure ? (
                        <Styled.TableCell hasBottomBorder={isLastFacCell}>
                          {isFirstFacLabel &&
                            renderUser(facilityGroup.user, 'facilityManager', code)}
                        </Styled.TableCell>
                      ) : (
                        <Styled.TableCell hasBottomBorder>
                          {renderUser(facilityGroup.user, 'facilityManager', code)}
                        </Styled.TableCell>
                      )}

                      {tableView === OrgTableTab.Structure ? (
                        <Styled.TableCell hasBottomBorder={isLastCode}>
                          {isFirstSupLabel && renderUser(supervisorGroup.user, 'supervisor', code)}
                        </Styled.TableCell>
                      ) : (
                        <Styled.TableCell hasBottomBorder>
                          {renderUser(supervisorGroup.user, 'supervisor', code)}
                        </Styled.TableCell>
                      )}

                      <Styled.TableCell hasBottomBorder>
                        <Typography>
                          {`${code.specificState || '-'}/
                          ${code.specificUnitName || '-'}/
                          ${code.code}`}
                        </Typography>
                      </Styled.TableCell>
                    </Styled.TableRow>
                  )
                })
              })
            })
          })
        })}
      </Styled.Table>
    </Stack>
  )
}
